import React, { useState } from 'react';
import {
    Box, Button, CircularProgress, List, ListItem, ListItemIcon, ListItemText, MenuItem,
    TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermission } from '../../hooks/usePermission';
import { AuthService } from '../../services/auth.service';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { WageSheetFormat } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { MONTH_NAMES, sanitizeFilenameSegment, triggerDownload, WAGE_SHEET_FORMATS } from './shared';

interface Props {
    institutionId: string;
}

// An audit pack covers a CLOSED month (finalized payroll, full wage sheet), so open on last month.
const lastMonthDate = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d; };
const defaultYear = () => lastMonthDate().getFullYear();
const defaultMonth = () => lastMonthDate().getMonth() + 1;

/**
 * Static description of the one-click audit pack's contents (decision note
 * docs/decisions/2026-09-11-garments-audit-wage-sheet-and-audit-pack.md §3(B)). Each piece is
 * gated on its OWN permission server-side by AuditPackService -- a caller missing one piece's
 * key still gets the rest of the pack with a "not included" manifest line -- so this list is
 * rendered greyed-out (not hidden) for a piece the caller lacks, matching that same-pack-minus-one
 * behaviour rather than implying the whole download is blocked.
 */
const AUDIT_PACK_ITEMS: Array<{ key: string; label: string; permission: string }> = [
    { key: '01', label: 'Wage sheet', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '02', label: 'Overtime register', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '03', label: 'Hour-breach report', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '04', label: 'Wage-compliance register', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '05', label: 'Attendance monthly export', permission: PERMISSIONS.ATTENDANCE_EXPORT },
    { key: '06', label: 'Leave register', permission: PERMISSIONS.LEAVE_BALANCE_VIEW_ALL },
    { key: '07', label: 'Bonus register', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '08', label: 'WPPF register', permission: PERMISSIONS.PAYROLL_VIEW },
    { key: '09', label: 'Insurance register', permission: PERMISSIONS.INSURANCE_VIEW },
    { key: '10', label: 'Combined payslips PDF (only when the month is FINALIZED)', permission: PERMISSIONS.PAYROLL_VIEW },
];

const AuditPackTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(defaultYear());
    const [month, setMonth] = useState(defaultMonth());
    const [format, setFormat] = useState<WageSheetFormat>('FULL');
    const [downloading, setDownloading] = useState(false);

    const canPayroll = usePermission(PERMISSIONS.PAYROLL_VIEW);
    const canAttendanceExport = usePermission(PERMISSIONS.ATTENDANCE_EXPORT);
    const canLeave = usePermission(PERMISSIONS.LEAVE_BALANCE_VIEW_ALL);
    const canInsurance = usePermission(PERMISSIONS.INSURANCE_VIEW);

    const permissionMap: Record<string, boolean> = {
        [PERMISSIONS.PAYROLL_VIEW]: canPayroll,
        [PERMISSIONS.ATTENDANCE_EXPORT]: canAttendanceExport,
        [PERMISSIONS.LEAVE_BALANCE_VIEW_ALL]: canLeave,
        [PERMISSIONS.INSURANCE_VIEW]: canInsurance,
    };

    const handleDownload = async () => {
        if (Number.isNaN(year) || Number.isNaN(month)) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadAuditPack(institutionId, year, month, format);
            const codeSegment = sanitizeFilenameSegment(AuthService.getSelectedInstitutionCode(), 'institution');
            const monthSegment = String(month).padStart(2, '0');
            triggerDownload(blob, `audit-pack-${codeSegment}-${year}-${monthSegment}.zip`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box>
            <Box display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    data-help="statutory.audit.year"
                    type="number"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        setYear(Number.isNaN(v) ? NaN : v);
                    }}
                    sx={{ flex: '1 1 120px', maxWidth: 192 }}
                />
                <TextField
                    select size="small" label="Month" data-help="statutory.audit.month" value={month}
                    onChange={e => setMonth(parseInt(e.target.value as string, 10))}
                    sx={{ flex: '1 1 150px', maxWidth: 240 }}
                >
                    {MONTH_NAMES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <TextField
                    select size="small" label="Wage Sheet Format" data-help="statutory.audit.format" value={format}
                    onChange={e => setFormat(e.target.value as WageSheetFormat)}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    {WAGE_SHEET_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </TextField>
                <Button
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={downloading}
                    data-testid="audit-pack-download"
                    data-help="statutory.audit.download"
                >
                    {downloading ? <CircularProgress size={16} /> : 'Download audit pack'}
                </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={1}>
                The pack always contains 00-MANIFEST.txt plus everything below that you have access to;
                anything you do not have access to is listed as "not included" in the manifest rather than
                blocking the whole download.
            </Typography>

            <List dense data-testid="audit-pack-checklist" data-help-section="statutory.audit.checklist">
                {AUDIT_PACK_ITEMS.map(item => {
                    const allowed = permissionMap[item.permission] ?? false;
                    return (
                        <ListItem
                            key={item.key}
                            data-testid={`audit-pack-item-${item.key}`}
                            data-help="statutory.audit.item"
                            sx={{ opacity: allowed ? 1 : 0.5 }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {allowed
                                    ? <CheckCircleIcon color="success" fontSize="small" />
                                    : <CancelIcon color="disabled" fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={`${item.key} -- ${item.label}`}
                                secondary={allowed ? undefined : `Requires ${item.permission}`}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

export default AuditPackTab;
